/**
 * InlineRecorder — a compact record/re-record control that lives
 * inside a row in the My Library page.
 * ─────────────────────────────────────────────────────────────────
 * Feb 27 2026 · Michael flagged that the "Affirmations in Need of
 * Recording" section had a mic *icon* but no actual record action,
 * and there was no way to re-bind (re-record) an already-recorded
 * line either. This component is the fix.
 *
 * States: idle → recording → uploading → done | error
 *
 * Uses the same `POST /api/user-voice-bank/record` endpoint the
 * existing VoiceLibraryPage recovery flow uses so we don't fork the
 * server side. On success the parent re-fetches so the line moves
 * out of "in need of recording" and into "my recorded affirmations".
 */
import React from "react";
import axios from "axios";
import { MicHelp } from "../../components/coach/MicHelp";

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem("reclaim_token");

const TOK = {
  cream: "#f5e0ad",
  creamMist: "rgba(245,224,173,0.55)",
  gold: "#e7c98a",
  goldSoft: "rgba(231,201,138,0.85)",
  goldMist: "rgba(231,201,138,0.28)",
  goldGlow: "rgba(231,201,138,0.10)",
  red: "#ff7a7a",
  ok: "#7ec59a",
};

export default function InlineRecorder({
  text,
  section,
  variant = "record",  // "record" | "rerecord"
  onDone,
  testIdPrefix = "inline-recorder",
}) {
  const [state, setState] = React.useState("idle");
  const [err, setErr] = React.useState(null);

  const recRef = React.useRef(null);
  const chunksRef = React.useRef([]);
  const streamRef = React.useRef(null);

  const cleanup = () => {
    try { streamRef.current?.getTracks?.().forEach((t) => t.stop()); } catch (_) {}
    streamRef.current = null;
    recRef.current = null;
    chunksRef.current = [];
  };
  React.useEffect(() => cleanup, []);

  const start = async () => {
    setErr(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      setErr("Your browser can't reach the microphone.");
      setState("error");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = typeof MediaRecorder !== "undefined" &&
        MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";
      const rec = new MediaRecorder(stream, { mimeType });
      recRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = () => upload();
      rec.start();
      setState("recording");
    } catch (e) {
      console.error("[inline-recorder] mic denied:", e);
      setErr(e?.name === "NotFoundError" ? "nomic" : "blocked");
      setState("error");
    }
  };

  const stop = () => {
    try { recRef.current?.stop(); } catch (_) {}
  };

  const upload = async () => {
    setState("uploading");
    try {
      const blob = new Blob(chunksRef.current, { type: "audio/webm" });
      const fd = new FormData();
      // Backend endpoint (see backend/routes/audio_journeys.py L840):
      //   POST /api/audio-journeys/voice-clips
      //   fields: file, line_text, section (in SECTION_IDS)
      fd.append("line_text", text);
      fd.append("section", (section || "").toLowerCase());
      fd.append("file", blob, "recording.webm");
      const r = await axios.post(`${API}/api/audio-journeys/voice-clips`, fd, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      cleanup();
      setState("done");
      onDone?.(r.data);
    } catch (e) {
      console.error("[inline-recorder] upload failed:", e);
      const detail = e?.response?.data?.detail;
      setErr(
        detail === "empty_voice_file" || detail === "audio_too_small" || detail === "audio_silent"
          ? "I couldn't hear anything. Try again a little closer to the phone."
          : detail === "unsupported_voice_format" || detail === "audio_format_unrecognised"
            ? "Couldn't read that recording. Please try once more."
            : detail?.startsWith?.("unknown_section")
              ? "This line has no section attached, cannot save."
              : "Something got in the way. Please try again."
      );
      cleanup();
      setState("error");
    }
  };

  const isRerecord = variant === "rerecord";
  const label =
    state === "recording" ? "Stop"
    : state === "uploading" ? "Saving…"
    : state === "done" ? (isRerecord ? "Re-recorded" : "Recorded")
    : isRerecord ? "Re-record" : "Record";

  const bg =
    state === "recording" ? "rgba(255,122,122,0.14)"
    : state === "done"    ? "rgba(126,197,154,0.14)"
    : TOK.goldGlow;

  const border =
    state === "recording" ? TOK.red
    : state === "done"    ? TOK.ok
    : TOK.gold;

  const fg =
    state === "recording" ? TOK.red
    : state === "done"    ? TOK.ok
    : TOK.gold;

  const onTap =
    state === "idle" || state === "error" || state === "done" ? start
    : state === "recording" ? stop
    : undefined;

  return (
    <div className="flex flex-col items-end gap-1">
      <button
        type="button"
        onClick={onTap}
        disabled={state === "uploading"}
        data-testid={`${testIdPrefix}-btn`}
        aria-label={label}
        className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 transition-all"
        style={{
          background: bg,
          border: `1px solid ${border}`,
          color: fg,
          fontFamily: '"Manrope", sans-serif',
          fontSize: "0.6rem",
          letterSpacing: "0.24em",
          textTransform: "uppercase",
          fontWeight: 600,
          cursor: state === "uploading" ? "wait" : "pointer",
          opacity: state === "uploading" ? 0.7 : 1,
        }}
      >
        {state === "recording" ? (
          <span
            style={{
              display: "inline-block",
              width: 7, height: 7, borderRadius: "50%",
              background: TOK.red,
              animation: "inline-rec-pulse 1s ease-in-out infinite",
            }}
          />
        ) : (
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
            <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
            <line x1="12" y1="19" x2="12" y2="23" />
          </svg>
        )}
        {label}
      </button>
      {(err === "blocked" || err === "nomic") && <div className="w-full mt-2"><MicHelp kind={err} onRetry={start} /></div>}
      {err && err !== "blocked" && err !== "nomic" && (
        <span
          className="text-[0.62rem] italic"
          style={{ color: TOK.red, fontFamily: '"Cormorant Garamond", serif' }}
          data-testid={`${testIdPrefix}-err`}
        >
          {err}
        </span>
      )}
      <style>{`@keyframes inline-rec-pulse { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.4; transform: scale(0.85); } }`}</style>
    </div>
  );
}
